import { format } from "@suse-o11y/date-time";
import { Dialog, DialogClose, DialogDescription, DialogTitle } from "@suse-o11y/dialog";
import { IconArrowDown, IconBack, IconClose, IconEdit, IconHamburger, IconPause } from "@suse-o11y/icons";
import React, { useCallback, useRef, useState } from "react";

import { useQuery } from "../../queries/react-query";
import type { BorgAgentType } from "../models/borg-types";
import { queryBorgAgents } from "../queries/borg-agents";

import { AgentModeSelector } from "./_agent-mode-selector";
import { AssistantAvatar } from "./_assistant-avatar";
import type { ExportData, FooterInputConfig } from "./_chat-drawer-content";
import { ChatDrawerContent } from "./_chat-drawer-content";
import { ContextBar, type ContextBarConfig } from "./_context-bar";
import { FollowUpInput } from "./_follow-up-input";
import { VerifyDisclaimer } from "./_verify-disclaimer";
import type { AiAssistantInput } from "./build-question";
import { setLastConversationId } from "./drawer-memory";
import { buildTranscriptLines } from "./transcript-utils";

const EMPTY_FOOTER: FooterInputConfig = {
	show: false,
	disabled: false,
	placeholder: "",
	showModeSelector: true,
	showStop: false,
	stopLabel: undefined,
};

// biome-ignore lint/suspicious/noEmptyBlockStatements: noop placeholder
const EMPTY_CONTEXT_BAR: ContextBarConfig = { pins: [], onRemovePin: () => {}, onAddPin: () => {}, removedPins: [] };

const downloadTranscript = (data: ExportData) => {
	const lines = buildTranscriptLines(data);
	const blob = new Blob([lines.join("\n")], { type: "text/markdown" });
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = `session-${format(Date.now(), "yyyy-MM-dd")}.md`;
	a.click();
	URL.revokeObjectURL(url);
};

const useDrawerState = () => {
	const [inputValue, setInputValue] = useState("");
	const [historyOpen, setHistoryOpen] = useState(false);
	const [footerConfig, setFooterConfig] = useState<FooterInputConfig>(EMPTY_FOOTER);
	const [contentResetSeed, setContentResetSeed] = useState(0);
	const [hasExportableContent, setHasExportableContent] = useState(false);
	const [selectedMode, setSelectedMode] = useState<BorgAgentType>("adaptive");
	const [contextBarConfig, setContextBarConfig] = useState<ContextBarConfig>(EMPTY_CONTEXT_BAR);
	// biome-ignore lint/suspicious/noEmptyBlockStatements: noop placeholder until child mounts
	const submitRef = useRef<(text: string) => void>(() => {});
	// biome-ignore lint/suspicious/noEmptyBlockStatements: noop placeholder until child mounts
	const stopRef = useRef<() => void>(() => {});
	const exportDataRef = useRef<ExportData | null>(null);

	const handleNewConversation = useCallback(() => {
		setInputValue("");
		setSelectedMode("adaptive");
	}, []);

	const resetContent = useCallback(() => {
		setLastConversationId(undefined);
		setInputValue("");
		setHasExportableContent(false);
		exportDataRef.current = null;
		setSelectedMode("adaptive");
		setContentResetSeed((value) => value + 1);
	}, []);

	return {
		inputValue,
		setInputValue,
		historyOpen,
		setHistoryOpen,
		footerConfig,
		setFooterConfig,
		contentResetSeed,
		hasExportableContent,
		setHasExportableContent,
		selectedMode,
		setSelectedMode,
		contextBarConfig,
		setContextBarConfig,
		submitRef,
		stopRef,
		exportDataRef,
		handleNewConversation,
		resetContent,
	};
};

export const AiAssistantDrawer: React.FC<AiAssistantInput> = (props) => (
	<Dialog className="drawer/dialog drawer-wide" data-beest-testid="ai-assistant-drawer">
		<DrawerContent {...props} />
	</Dialog>
);

const DrawerContent: React.FC<AiAssistantInput> = (props) => {
	const state = useDrawerState();
	const showAgentSelector = state.footerConfig.show && state.footerConfig.showModeSelector;
	const { data: agents } = useQuery({ ...queryBorgAgents, enabled: showAgentSelector });

	return (
		<>
			<header className="drawer-header py-0">
				<AssistantAvatar />

				<div className="mr-auto flex flex-col">
					<DialogTitle className="m-0 font-medium text-grey-900 text-sm">
						{state.historyOpen ? "Session History" : "Chat with Liz"}
					</DialogTitle>

					{!state.historyOpen && (
						<DialogDescription className="m-0 text-graphite-500 text-xs">AI Assistant</DialogDescription>
					)}
				</div>

				{state.historyOpen ? (
					<button
						aria-label="Back to chat"
						className="button button-tertiary gap-2 uppercase"
						onClick={() => state.setHistoryOpen(false)}
						type="button"
					>
						<IconBack className="size-4" />
						<span className="text-xs">Back</span>
					</button>
				) : (
					<>
						<button
							aria-label="Chat history"
							className="button button-square"
							onClick={() => state.setHistoryOpen(true)}
							type="button"
						>
							<IconHamburger className="size-4" />
						</button>

						<button
							aria-label="Download transcript"
							className="button button-square"
							disabled={!state.hasExportableContent}
							onClick={() => state.exportDataRef.current && downloadTranscript(state.exportDataRef.current)}
							type="button"
						>
							<IconArrowDown className="size-4" />
						</button>

						<button
							aria-label="New conversation"
							className="button button-square"
							onClick={state.resetContent}
							type="button"
						>
							<IconEdit className="size-4" />
						</button>
					</>
				)}

				<DialogClose aria-label="Close" className="button button-square">
					<IconClose className="size-3" />
				</DialogClose>
			</header>

			<section className="drawer-body">
				<ChatDrawerContent
					content={props}
					exportDataRef={state.exportDataRef}
					historyOpen={state.historyOpen}
					key={`chat-${state.contentResetSeed}`}
					onHistoryClose={() => state.setHistoryOpen(false)}
					onNewConversation={state.handleNewConversation}
					onResend={state.setInputValue}
					selectedMode={state.selectedMode}
					setContextBarConfig={state.setContextBarConfig}
					setFooterConfig={state.setFooterConfig}
					setHasExportableContent={state.setHasExportableContent}
					stopRef={state.stopRef}
					submitRef={state.submitRef}
				/>
			</section>

			{!state.historyOpen && (
				<footer className="drawer-footer flex-col items-stretch gap-2">
					{(state.contextBarConfig.pins.length > 0 || state.contextBarConfig.removedPins.length > 0) && (
						<ContextBar
							onAddPin={state.contextBarConfig.onAddPin}
							onRemovePin={state.contextBarConfig.onRemovePin}
							pins={state.contextBarConfig.pins}
							removedPins={state.contextBarConfig.removedPins}
						/>
					)}
					<div hidden={!state.footerConfig.show}>
						<FollowUpInput
							disabled={state.footerConfig.disabled}
							onChange={state.setInputValue}
							onSubmit={(text) => {
								state.setInputValue("");
								state.submitRef.current(text);
							}}
							placeholder={state.footerConfig.placeholder}
							value={state.inputValue}
							visible={state.footerConfig.show}
						/>
					</div>
					{state.footerConfig.showStop && (
						<button
							className="flex cursor-pointer items-center justify-center gap-2 self-start rounded-full border border-grey-400 bg-white px-3 py-1.5 text-grey-800 text-xs shadow-sm transition-colors hover:border-primary-500 hover:text-primary-500"
							onClick={() => state.stopRef.current()}
							type="button"
						>
							<IconPause className="size-3.5" />
							<span>{state.footerConfig.stopLabel ?? "Stop"}</span>
						</button>
					)}
					{showAgentSelector && (
						<div className={state.footerConfig.disabled ? "opacity-70" : undefined}>
							<AgentModeSelector agents={agents} onChange={state.setSelectedMode} selected={state.selectedMode} />
						</div>
					)}
					<VerifyDisclaimer />
				</footer>
			)}
		</>
	);
};
